import { useState, useEffect } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { userRequest } from '../utils/requestMethods';
import { Header } from '../components';
import { useStateContext } from '../contexts/ContextProvider';
import { FiArrowLeft, FiArrowRight, FiDownload, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { MdSearch } from 'react-icons/md';
import DataTable from 'react-data-table-component';
import { formatCurrency } from '../utils/currencyUtils';
import { exportToCSV } from '../utils/exportCSV';
import SkeletonTable from '../components/common/SkeletonTable';
import CustomPagination from '../components/CustomPagination/CustomPagination';

const customStyles = {
  headRow: {
    style: { backgroundColor: 'rgb(248, 250, 252)', fontWeight: 'bold' },
  },
};

const PaymentPerAddon = () => {
  const history = useHistory();
  const location = useLocation();
  const addonId = location.pathname.split('/')[3];
  const addonName = decodeURIComponent(location.pathname.split('/')[4] || '');
  const { school } = useStateContext();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const schoolId = school?._id;

  useEffect(() => {
    if (addonId && schoolId) fetchPayments();
  }, [addonId, schoolId]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const { data } = await userRequest.get(
        `/payments/school/${schoolId}/addon/${addonId}/payments?term=${school.term}&session=${encodeURIComponent(school.session)}&limit=100&sort_by=date&order=desc`
      );
      setTransactions(data.records || []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // Summary stats
  const totalStudents = transactions.length;
  const totalCollected = transactions.reduce((sum, t) => sum + Number(t.addon?.amount_paid || 0), 0);
  const fullyPaid = transactions.filter(t => t.addon?.is_fully_paid).length;
  const outstanding = totalStudents - fullyPaid;

  const columns = [
    {
      name: 'Student',
      cell: row => (
        <div>
          <p className="text-sm font-medium text-gray-800">
            {row.child ? `${row.child.firstname} ${row.child.lastname}` : 'N/A'}
          </p>
          <p className="text-xs text-gray-400">{row.class?.class_name || ''}</p>
        </div>
      ),
      grow: 2,
      exportCell: row => row.child ? `${row.child.firstname} ${row.child.lastname}` : 'N/A',
    },
    {
      name: 'Fee',
      cell: row => <span className="text-sm text-gray-700">{formatCurrency(row.addon?.add_on_price)}</span>,
      exportCell: row => formatCurrency(row.addon?.add_on_price),
    },
    {
      name: 'Paid',
      cell: row => <span className="text-sm font-medium text-green-700">{formatCurrency(row.addon?.amount_paid)}</span>,
      exportCell: row => formatCurrency(row.addon?.amount_paid),
    },
    {
      name: 'Balance',
      cell: row => {
        const balance = Number(row.addon?.add_on_price || 0) - Number(row.addon?.amount_paid || 0);
        return (
          <span className={`text-sm font-medium ${balance > 0 ? 'text-red-600' : 'text-gray-400'}`}>
            {formatCurrency(balance > 0 ? balance : 0)}
          </span>
        );
      },
      exportCell: row => formatCurrency(Math.max(0, Number(row.addon?.add_on_price || 0) - Number(row.addon?.amount_paid || 0))),
    },
    {
      name: 'Status',
      cell: row => (
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1
          ${row.addon?.is_fully_paid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}
        >
          {row.addon?.is_fully_paid ? <FiCheckCircle className="w-3 h-3" /> : <FiAlertCircle className="w-3 h-3" />}
          {row.addon?.is_fully_paid ? 'Paid' : 'Outstanding'}
        </span>
      ),
      exportCell: row => row.addon?.is_fully_paid ? 'Paid' : 'Outstanding',
    },
    {
      cell: row => (
        <Link to={`/class-transaction/payment/${row.payment_id}/${row.child?._id}`}>
          <button className="p-2 rounded-full text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors">
            <FiArrowRight className="w-4 h-4" />
          </button>
        </Link>
      ),
      ignoreRowClick: true,
      button: true,
      width: '56px',
    },
  ];

  const filteredData = transactions.filter(item =>
    `${item.child?.firstname} ${item.child?.lastname}`.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen">
      <button
        onClick={() => history.goBack()}
        className="inline-flex items-center gap-2 mb-4 text-sm font-medium text-gray-500 hover:text-indigo-600 transition-colors"
      >
        <FiArrowLeft className="w-4 h-4" />
        Back
      </button>

      <div className="mb-6">
        <Header category="Extra Fees" title={addonName} />
        <p className="text-sm text-gray-400 -mt-2">
          {school?.term} — {school?.session}
        </p>
      </div>

      {/* Summary cards */}
      {!loading && transactions.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
            <p className="text-xs text-gray-500 mb-1">Total Students</p>
            <p className="text-xl font-bold text-gray-900">{totalStudents}</p>
          </div>
          <div className="rounded-2xl border border-green-100 bg-green-50 px-4 py-4">
            <p className="text-xs text-gray-500 mb-1">Fully Paid</p>
            <p className="text-xl font-bold text-green-700">{fullyPaid}</p>
          </div>
          <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-4">
            <p className="text-xs text-gray-500 mb-1">Outstanding</p>
            <p className="text-xl font-bold text-amber-700">{outstanding}</p>
          </div>
          <div className="rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-4">
            <p className="text-xs text-gray-500 mb-1">Total Collected</p>
            <p className="text-lg font-bold text-indigo-700">{formatCurrency(totalCollected)}</p>
          </div>
        </div>
      )}

      {/* Search + Export */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <MdSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            placeholder="Search students..."
            className="pl-9 pr-4 py-2 text-sm rounded-full bg-gray-50 border border-gray-200 focus:ring-indigo-500 focus:border-indigo-500 w-48 md:w-64"
          />
        </div>
        <button
          onClick={() => exportToCSV(filteredData, columns, `${addonName}-payments.csv`)}
          className="inline-flex items-center gap-2 py-2 px-4 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-full transition-colors"
        >
          <FiDownload className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-2xl overflow-hidden">
        <DataTable
          data={filteredData}
          columns={columns}
          getRowId={row => row.payment_id}
          pagination
          paginationComponent={CustomPagination}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={<SkeletonTable rows={5} columns={5} />}
          highlightOnHover
          persistTableHead
          noDataComponent={
            <div className="py-14 text-center text-sm text-gray-400">No payment records found for this fee.</div>
          }
        />
      </div>
    </div>
  );
};

export default PaymentPerAddon;
